import React, { useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import ChildrenRenderer from "./ChildrenRenderer";
import RenderHtml from "../../../../common/AppSpecificConpnents/PartsTable/RenderHtml";
import { pingTask } from "../../../../util/apiService";
import { cleanBaselineHtml } from "../../../../util/lexical";
import {
  NODE_SELECTION_BLUE,
  getHaloWrapperStyle,
  getHaloCheckboxStyle,
  isDescendantLockedPreview,
  getDescendantLockedCheckboxStyle,
  auditPreviewSectionDescendantWrapStyle,
} from "./nodeSelectionCheckboxStyles";

const SubstepNode = ({
  substep,
  substepIndex,
  globalStepStart = 0,
  stepIndex,
  sectionIndex,
  globalStepNumber,
  sectionHeading,
  stepHeading,
  sectionId,
  stepId,
  documentType = "",
  documentPartNumber = "",
  documentRevision = "",
  onNavigate,
  checkboxState,
  disabled = false,
  showCheckbox = true,
  showOnlySectionCheckboxes = false,
  checkboxLabelColor = null,
  allNodesData = [],
  showSectionCheckbox = true,
  hideChildCheckboxes = false,
  forceShowChildHeadings = false,
  hideSubstepCheckboxes = false,
  isLock = false,
  isRevisionLocked = false,
  showHeading = null,
  hideChildHeadingsForTypes = null,
  enableCanvasStagePopup = false,
  /** Audit preview: source substep from "User Copied Substep 2A from …" (destination tree may be 1A). */
  auditSubstepHeading = null,
  /** Full breadcrumb for source path, e.g. "Section 2 > Step 2 > Substep 2A". */
  auditSubstepBreadcrumb = null,
}) => {
  const authUser = useSelector((state) => state.auth.user);
  let substepLetter = String.fromCharCode(65 + substepIndex); // A, B, C...
  if (substep.is_deleted) {
    substepLetter = "(Deleted)";
  }
  const globalSubstepLabel = `Substep ${!substep.is_deleted ? globalStepNumber : ""}${substepLetter}`;
  const displaySubstepTitle =
    typeof auditSubstepHeading === "string" && auditSubstepHeading.trim()
      ? auditSubstepHeading.trim()
      : globalSubstepLabel;

  // Get revisionId and authUser for ping API
  const location = useLocation();
  const params = useParams();

  // Extract revisionId from params (could be revisionId or revision_id depending on route)
  const revisionId = params.revisionId || params.revision_id;

  // Check if we're on a view-revision route
  const isViewRevisionRoute =
    location.pathname.includes("/view-revision") ||
    location.pathname.includes("/view-section") ||
    location.pathname.includes("/view-step") ||
    location.pathname.includes("/view-substep") ||
    location.pathname.includes("/view-childnode");

  // Start ping interval if on view-revision route
  useEffect(() => {
    if (!isViewRevisionRoute || !revisionId || !authUser) return;

    const interval = setInterval(async () => {
      try {
        const pingData = {
          task_id: revisionId,
          user_id: authUser.id,
        };
        await pingTask(pingData);
      } catch (error) {
        console.error("Ping failed in SubstepNode:", error);
      }
    }, 60000); // 60 seconds = 1 minute

    return () => {
      clearInterval(interval);
    };
  }, [isViewRevisionRoute, revisionId, authUser]);

  // Determine checkbox state for this substep (focus id may be tree row or entity ref from audit row)
  const focusId = checkboxState?.currentNodeId;
  const d = substep.detail || {};
  const isCurrent =
    checkboxState?.currentNodeType === "substep" &&
    !!focusId &&
    (focusId === substep.tree_node_id ||
      focusId === substep.substep_id_ref ||
      focusId === d.sub_step_tree_node_id);
  const isSourceSubstep =
    checkboxState?.sourceNodeId &&
    (checkboxState?.sourceNodeId === substep.tree_node_id ||
      checkboxState?.sourceNodeId === substep.substep_id_ref);
  /** Bulk `allChildrenChecked` is for descendants (e.g. rich text locked tiles); the substep row stays `isCurrent`-only here. */
  const auditSubstepFocusRow =
    checkboxState?.auditPreviewCurrentNode === true &&
    checkboxState?.currentNodeType === "substep";
  const isChecked = !!(
    isCurrent ||
    isSourceSubstep ||
    (!auditSubstepFocusRow && checkboxState?.allChildrenChecked)
  );
  const isDescendantLocked = isDescendantLockedPreview(
    checkboxState,
    isCurrent,
    isChecked,
    !!isSourceSubstep,
  );
  const isDisabled =
    disabled ||
    (checkboxState?.allChildrenDisabled && !isCurrent) ||
    (isRevisionLocked &&
      isRevisionLocked.lock &&
      isRevisionLocked.lock.user_id !== authUser?.id);

  const useSelectionHalo =
    isCurrent &&
    isChecked &&
    !isSourceSubstep &&
    checkboxState?.auditPreviewCurrentNode === true;

  const checkboxOnChange = () => {
    if (isSourceSubstep) return;
    if (!isDisabled && onNavigate) {
      onNavigate("substep", substep, {
        substepIndex,
        stepIndex,
        sectionIndex,
        globalStepNumber,
        substepLetter,
        sectionHeading,
        stepHeading,
        substepHeading: displaySubstepTitle,
        sectionId,
        stepId,
        substepId: substep.tree_node_id,
      });
    }
  };

  const defaultCheckboxStyle = {
    cursor: isDisabled
      ? "not-allowed"
      : isSourceSubstep
        ? "not-allowed"
        : "pointer",
    accentColor: isSourceSubstep
      ? "rgb(68, 68, 68)"
      : isChecked
        ? NODE_SELECTION_BLUE
        : undefined,
    backgroundColor: isSourceSubstep ? "rgb(68, 68, 68)" : undefined,
  };

  return (
    <div
      key={substep.tree_node_id}
      data-export-block="substep"
      style={{ marginTop: "20px", clear: "both" }}
    >
      <div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "8px",
              alignItems: "center",
            }}
          >
            {showCheckbox && (
              <div
                style={{
                  position: "relative",
                  display: "inline-block",
                  ...auditPreviewSectionDescendantWrapStyle(checkboxState),
                }}
              >
                {isDescendantLocked ? (
                  <input
                    type="checkbox"
                    checked={isChecked}
                    disabled
                    readOnly
                    tabIndex={-1}
                    aria-readonly="true"
                    onChange={() => {}}
                    style={getDescendantLockedCheckboxStyle()}
                  />
                ) : useSelectionHalo ? (
                  <div style={getHaloWrapperStyle()}>
                    <input
                      type="checkbox"
                      checked={isChecked}
                      disabled={isDisabled}
                      onChange={checkboxOnChange}
                      style={getHaloCheckboxStyle({
                        disabled: isDisabled,
                        isSource: isSourceSubstep,
                      })}
                    />
                  </div>
                ) : (
                  <input
                    type="checkbox"
                    checked={isChecked}
                    disabled={isDisabled}
                    onChange={checkboxOnChange}
                    style={defaultCheckboxStyle}
                  />
                )}
                {/* Red cross icon overlay - shown when disabled and hovering */}
                {isDisabled && !isDescendantLocked && (
                  <div
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      opacity: 0,
                      transition: "opacity 0.2s ease-in-out",
                      pointerEvents: "none",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100%",
                      height: "100%",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.opacity = "1";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.opacity = "0";
                    }}
                    className="checkbox-hover-overlay"
                  >
                    <FontAwesomeIcon
                      icon={faTimes}
                      size="sm"
                      color="red"
                      style={{
                        filter: "drop-shadow(0 0 1px rgba(255,255,255,0.8))",
                      }}
                    />
                  </div>
                )}
              </div>
            )}
            <h2
              onClick={() => {
                // Prevent action for source substep being moved
                if (isSourceSubstep) {
                  return;
                }
                if (!isDisabled && showCheckbox && onNavigate) {
                  onNavigate("substep", substep, {
                    substepIndex,
                    stepIndex,
                    sectionIndex,
                    globalStepNumber,
                    substepLetter,
                    sectionHeading,
                    stepHeading,
                    substepHeading: displaySubstepTitle,
                    sectionId,
                    stepId,
                    substepId: substep.tree_node_id,
                  });
                }
              }}
              style={{
                fontSize: "20px",
                margin: 0,
                fontWeight: useSelectionHalo || isDescendantLocked ? 700 : undefined,
                color: useSelectionHalo || isDescendantLocked
                  ? "#111"
                  : showCheckbox
                    ? checkboxLabelColor || undefined
                    : undefined,
                cursor: isSourceSubstep
                  ? "not-allowed"
                  : !isDisabled && showCheckbox
                    ? "pointer"
                    : "default",
                userSelect: "none",
              }}
            >
              {displaySubstepTitle}
            </h2>
          </div>

          <div
            style={{
              color: "#888",
              fontSize: "14px",
              fontWeight: "normal",
            }}
          >
            {!substep.is_deleted &&
              (typeof auditSubstepBreadcrumb === "string" &&
              auditSubstepBreadcrumb.trim() ? (
                <>{auditSubstepBreadcrumb.trim()}</>
              ) : (
                <>
                  Section {sectionIndex + 1} &gt; Step {globalStepNumber} &gt;
                  Substep {globalStepNumber}
                  {substepLetter}
                </>
              ))}
          </div>
        </div>

        {(() => {
                      const rawShort = cleanBaselineHtml(
                        substep.detail?.short_description || ""
                      );
                      // Convert plain text with quotes to HTML format for proper rendering
                      const contentHtml =
                        typeof rawShort === "string" && rawShort.includes('class="editor-shell"')
                          ? rawShort.replace(/^<div class="editor-shell">(.*)<\/div>\s*$/s, "$1")
                          : rawShort && rawShort.trim() !== ""
                            ? rawShort.includes('<') && rawShort.includes('>')
                              ? rawShort // Already HTML, use as-is
                              : `<p>${rawShort.replace(/"/g, '&quot;')}</p>` // Plain text, wrap in p tags
                            : rawShort;
                      return contentHtml ? (
                        <div
                          style={{ marginTop: "5px" }}
                          className="rich-text-content-display"
                        >
                          <RenderHtml content={contentHtml} />
                        </div>
                      ) : null;
                    })()}
      </div>

      {/* Substep Children */}
      <ChildrenRenderer
        children={substep.children || []}
        parentContext={{
          sectionIndex,
          stepIndex,
          globalStepNumber,
          substepIndex,
          substepLetter,
          sectionId,
          stepId,
          substepId: substep.tree_node_id,
          documentType,
          documentPartNumber,
          documentRevision,
          auditSubstepBreadcrumbFull:
            typeof auditSubstepBreadcrumb === "string" &&
            auditSubstepBreadcrumb.trim()
              ? auditSubstepBreadcrumb.trim()
              : undefined,
        }}
        onNavigate={onNavigate}
        checkboxState={checkboxState}
        disabled={disabled}
        showCheckbox={showCheckbox && !hideChildCheckboxes}
        showHeading={
          forceShowChildHeadings
            ? true
            : hideChildCheckboxes
              ? true
              : showHeading
        }
        allNodesData={allNodesData}
        checkboxLabelColor={checkboxLabelColor}
        isLock={isLock}
        isRevisionLocked={isRevisionLocked}
        hideChildHeadingsForTypes={hideChildHeadingsForTypes}
        enableCanvasStagePopup={enableCanvasStagePopup}
      />
    </div>
  );
};

export default SubstepNode;
